import { Box, Typography } from '@coolblue-development/becky';
import { useState, useEffect, useRef, useCallback } from 'react';
import useClickOutside from '../../../../../../hooks/useClickOutside';
import { useEmailContext } from '../../../../../../contexts/EmailContext';
import { datadogLogs } from '@datadog/browser-logs';
import { MessageTemplateClient, MessageTemplate, MessageTemplateContent } from '@amazon-connect/message-template';
import { Button } from '@mui/material';
import { Skeleton, Loader } from '@coolblue-development/freddy';

const EmailMessageTemplates = (): JSX.Element => {
  const { selectedEmail, setTemplateHtml, templateHtml } = useEmailContext();
  const [showDropdown, setShowDropdown] = useState(false);
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const templateContentCache = useRef<Map<string, MessageTemplateContent>>(new Map());
  const messageTemplateClientRef = useRef<MessageTemplateClient | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const hasAutoSelected = useRef(false);

  const insertTemplateContent = useCallback(
    (templateContent: MessageTemplateContent): void => {
      if (templateContent.body?.html) {
        const textarea = document.createElement('textarea');
        textarea.innerHTML = templateContent.body.html;
        setTemplateHtml(textarea.value);
      } else if (templateContent.body?.plainText) {
        setTemplateHtml(`<p>${templateContent.body.plainText}</p>`);
      }
    },
    [setTemplateHtml]
  );

  useEffect(() => {
    try {
      messageTemplateClientRef.current = new MessageTemplateClient(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any
        connect.core.getSDKClientConfig() as any
      );
    } catch (error) {
      datadogLogs.logger.error('Failed to initialize MessageTemplateClient', { channel: 'email' }, error as Error);
    }
  }, []);

  const fetchTemplates = useCallback(async (): Promise<void> => {
    if (!messageTemplateClientRef.current) return;

    setLoading(true);
    try {
      const response = await messageTemplateClientRef.current.searchMessageTemplates({ channels: ['EMAIL'] });
      const sortedTemplates = [...response.messageTemplates].sort((a, b) => a.name.localeCompare(b.name));
      setTemplates(sortedTemplates);

      if (selectedEmail) {
        const prefetchTemplates = sortedTemplates.map(async template => {
          try {
            const templateContent = await messageTemplateClientRef.current!.getContent(
              template.messageTemplateArn,
              selectedEmail
            );
            templateContentCache.current.set(template.messageTemplateId, templateContent);
          } catch (error) {
            datadogLogs.logger.error(
              'Failed to prefetch template content',
              { templateName: template.name, channel: 'email' },
              error as Error
            );
          }
        });
        await Promise.all(prefetchTemplates);

        const defaultContent = templateContentCache.current.get(response.messageTemplates[0]?.messageTemplateId);
        if (!hasAutoSelected.current && defaultContent && !templateHtml) {
          insertTemplateContent(defaultContent);
          hasAutoSelected.current = true;
        }
      }
    } catch (error) {
      datadogLogs.logger.error('Failed to fetch message templates', { channel: 'email' }, error as Error);
    } finally {
      setLoading(false);
    }
  }, [insertTemplateContent, selectedEmail, templateHtml]);

  useEffect(() => {
    if (templateHtml) {
      return;
    }

    if (messageTemplateClientRef.current && templateContentCache.current.size === 0) {
      void fetchTemplates();
    }
  }, [fetchTemplates, templateHtml]);

  useEffect(() => {
    hasAutoSelected.current = false;
    templateContentCache.current.clear();
  }, [selectedEmail]);

  const handleToggle = (): void => {
    const next = !showDropdown;
    setShowDropdown(next);
    if (next && templateContentCache.current.size === 0) {
      void fetchTemplates();
    }
  };

  useClickOutside(containerRef, showDropdown, () => setShowDropdown(false));

  const handleTemplateSelect = async (template: MessageTemplate): Promise<void> => {
    if (!messageTemplateClientRef.current || !selectedEmail) return;

    try {
      const templateContent: MessageTemplateContent =
        templateContentCache.current.get(template.messageTemplateId) ??
        (await messageTemplateClientRef.current.getContent(template.messageTemplateArn, selectedEmail));

      insertTemplateContent(templateContent);
    } catch (error) {
      datadogLogs.logger.error('Failed to fetch template content', { selectedEmail, channel: 'email' }, error as Error);
    }

    setShowDropdown(false);
  };

  return (
    <Box ref={containerRef}>
      {loading && !hasAutoSelected.current && <Loader size="small" />}
      <Box sx={{ display: showDropdown ? 'block' : 'none' }}>
        <Box
          data-testid="email-message-templates-dropdown"
          sx={{
            position: 'absolute',
            bottom: 55,
            border: '1px solid #DDDDDD',
            padding: '15px',
            background: 'white',
            borderRadius: '8px',
            maxWidth: '600px',
            boxShadow: '0px 4px 4px 0px #00000040',
            display: 'flex',
            zIndex: 1
          }}
        >
          <Box sx={{ minWidth: '300px', maxHeight: '250px', overflow: 'auto' }}>
            <Typography sx={{ marginBottom: '10px' }} variant="h6">
              Signatures
            </Typography>
            {loading && <Skeleton height="2rem" />}
            {!loading &&
              templates.map(template => (
                <Typography
                  data-testid={`email-message-template-${template.messageTemplateId}`}
                  key={template.messageTemplateId}
                  sx={{
                    padding: '12px',
                    cursor: 'pointer',
                    borderRadius: '4px',
                    background: '#ffffff',
                    '&:hover': { background: '#F2F7FC' },
                    '&&': { margin: '0px' }
                  }}
                  onClick={() => void handleTemplateSelect(template)}
                >
                  {template.name}
                </Typography>
              ))}
          </Box>
        </Box>
      </Box>
      <Button
        data-testid="email-message-templates-button"
        sx={{
          height: '48px',
          padding: '0 16px',
          border: '1px solid #DDDDDD',
          background: showDropdown ? '#F2F7FC' : 'inherit',
          borderColor: showDropdown ? '#0090E3' : '#DDDDDD',
          marginRight: '8px'
        }}
        disabled={!selectedEmail}
        onClick={handleToggle}
      >
        Signatures
      </Button>
    </Box>
  );
};

export default EmailMessageTemplates;
